// BYO-01 copy: what each path is, and why one is closed. Provider terms from
// docs/research/2026-10-20-agents-in-chat.md §4 (a summary, not legal advice).

export const ANTHROPIC_TERMS_URL = "https://code.claude.com/docs/en/legal-and-compliance";
export const ANTHROPIC_COMMERCIAL_TERMS_URL = "https://www.anthropic.com/legal/commercial-terms";

export const PROVIDERS = [
  { value: "anthropic", label: "Anthropic", terms: ANTHROPIC_COMMERCIAL_TERMS_URL },
  { value: "openai", label: "OpenAI", terms: "https://openai.com/policies/services-agreement" },
  { value: "openrouter", label: "OpenRouter", terms: "https://openrouter.ai/terms" },
  // MODEL-KEYS-PLANS: one company key per provider; DeepSeek runs the light tier.
  {
    value: "deepseek",
    label: "DeepSeek",
    terms:
      "https://platform.deepseek.com/downloads/DeepSeek%20Open%20Platform%20Terms%20of%20Service.html",
  },
] as const;

export const providerLabel = (p: string) => PROVIDERS.find((x) => x.value === p)?.label ?? p;

export const COPY = {
  aiIntro:
    "Add your own provider API key to power your own agents and crew. It is sealed the moment you save it: CompanyOS never shows it again, only its last four characters.",
  cliTitle: "Your own Claude Code login",
  cliWhat:
    "Record that you use your own, logged-in Claude Code CLI (the vendor's unmodified app) for your own crew. CompanyOS stores no token: the login stays inside Claude Code on your device or your own runtime session.",
  cliOffWhy:
    "CompanyOS never asks for a Claude.ai login: Anthropic's terms keep a Pro or Max plan to the unmodified Claude Code app. This opens once Anthropic confirms its position and an admin turns it on.",
  pooledRefused:
    "A plan login is never shared, never used for someone else's work and never for paid client work.",
  keyNotice:
    "Use an API key from your provider's console (for Anthropic: Claude Console). A Claude.ai subscription token is refused.",
  connectionsIntro:
    "Connect a tool your agents and crew may call. A custom API key reaches one host you declare; every call it makes is approved first.",
  oauthNeedsSetup:
    "Needs admin setup: the server has no OAuth app for this service yet (a founder input).",
  channelsIntro:
    "Channels you have joined, how much each one notifies you, and the ones you may still join.",
  companyIntro:
    "Which subscription paths people may use here, and the company's own keys and connections.",
  oauthRefreshes:
    "Signed in with the service: CompanyOS renews its access token on its own before it expires.",
  crewMailCalendar:
    "Each person may let their own agent read their mail or calendar, through their own Google connection only.",
} as const;

/** Work OS `HEALTH_COPY` (lifted in @companyos/domain-agents/connect health.ts): one label and
 * one sentence per state. */
export const HEALTH: Record<
  string,
  {
    label: string;
    tone: "success" | "warning" | "danger" | "outline" | "neutral";
    sentence: string;
  }
> = {
  healthy: {
    label: "Healthy",
    tone: "success",
    sentence: "Working: a call or check succeeded in the last 7 days.",
  },
  unverified: {
    label: "Unverified",
    tone: "neutral",
    sentence:
      "Connected, but nothing has used or checked it recently. Check runs a read-only check.",
  },
  rate_limited: {
    label: "Rate-limited",
    tone: "warning",
    sentence: "The service asked CompanyOS to slow down. Calls resume automatically.",
  },
  reauth_required: {
    label: "Needs sign-in",
    tone: "warning",
    sentence: "The service no longer accepts this sign-in. Reconnect it to restore it.",
  },
  unreachable: {
    label: "Unreachable",
    tone: "danger",
    sentence:
      "The service is not reachable right now, or answered in a way CompanyOS refuses. Check it again.",
  },
  revoked: {
    label: "Revoked",
    tone: "danger",
    sentence:
      "The service refused this connection's sign-in, or it was removed from the account. Reconnect it.",
  },
  disconnected: {
    label: "Disconnected",
    tone: "outline",
    sentence: "Disconnected from CompanyOS. Its tokens were deleted.",
  },
};

/** M4-04: states an OAuth connection leaves by signing in again. */
export const NEEDS_RECONNECT = new Set(["reauth_required", "revoked", "unreachable"]);

/** Work OS `healthOf`: healthy with nothing verified in 7 days reads "Unverified". */
export function healthKey(
  c: {
    state: string;
    last_verified_at: string | null;
    last_checked_at: string | null;
    rate_limited_until?: string | null;
  },
  now = Date.now(),
): string {
  if (c.state === "rate_limited" && c.rate_limited_until && Date.parse(c.rate_limited_until) <= now)
    return "healthy";
  if (c.state !== "healthy") return c.state;
  const last = c.last_verified_at ? Date.parse(c.last_verified_at) : 0;
  return last > 0 && now - last <= 7 * 86_400_000 ? "healthy" : "unverified";
}
